import type {CSSProperties, ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import React from 'react';
import type {Block, CssRuleStyle} from 'grapesjs';
import {Text, TextSize} from '@Components/text';
import type {BlockCategoryMap} from '@Components/email-maker-block-manager/email-maker-block-manager.types';
import {EmailMakerBlock} from '@Components/email-maker-block-manager/components/email-maker-block';
import {getUniqueString} from '@Utils/string.util';
import {EmailComponentTemplates} from '@Components/email-maker-block-manager/components/email-component-templates/email-component-templates';
import {getBlocksFromCategories} from '@Libraries/email-component-template-library';
import styles from './text-block-panel.module.scss';
import {ElementsPanelHeader} from '@Components/email-maker-block-manager/components/elements-panel-header';
import {noop} from '@Utils/general.util';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';

interface TextBlockManagerProps {
  blocksMap: BlockCategoryMap;
  headerLabel?: string;
  showBackButton?: boolean;
  onBackButton?: VoidFunction;
}

const TEXT_TEMPLATE_CATEGORY_ID = 1;
const GA4_TRACKING_NAME = 'Text';

export function TextBlockPanel({blocksMap, headerLabel = '', showBackButton = false, onBackButton = noop}: TextBlockManagerProps): ReactElement {
  const BLOCK_CATEGORIES = [window.PMW.emails.blocks.CATEGORY_TEXT];
  const textBlocks = getBlocksFromCategories(blocksMap, BLOCK_CATEGORIES);
  const [showShadow, setShowShadow] = React.useState<boolean>(false);
  const panelContentRef = useRef<HTMLDivElement>(null);

  const getStyleProperty = (cssStyleProperty: keyof CssRuleStyle): keyof CSSProperties => {
    switch (cssStyleProperty) {
      case 'color':
        return 'color';
      case 'font-size':
        return 'fontSize';
      case 'font-weight':
        return 'fontWeight';
      case 'font-family':
        return 'fontFamily';
      case 'text-align':
        return 'textAlign';
      default:
        return cssStyleProperty as keyof CSSProperties;
    }
  };

  const getBlockTextLabelStyle = (block: Block): CSSProperties => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const textStyles = block.getContent()?.style as unknown as CssRuleStyle;

    if (!textStyles) {
      return {};
    }

    const stylesToCopy: (keyof CssRuleStyle)[] = ['color', 'font-size', 'font-weight', 'font-family', 'text-align'];

    return stylesToCopy.reduce((result: CSSProperties, key: string) => {
      if (textStyles[key]) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        result[getStyleProperty(key)] = textStyles[key];
      }
      return result;
    }, {});
  };

  const getBlockVisual = (block: Block): ReactElement => {
    return <div style={getBlockTextLabelStyle(block)}>{block.getLabel()}</div>;
  };

  const getDefaultTextBlocks = (): ReactElement => {
    return (
      <>
        <Text val={window.i18next.t('pmwjs_plain_text')} size={TextSize.SMALL} bold />
        <div className={styles.blocksGrid}>
          {textBlocks.map((block) => {
            return (
              <EmailMakerBlock block={block} extraClasses={styles.block} key={getUniqueString()} onAddToCanvas={trackGA4EventOnDefaultTextAdd}>
                {getBlockVisual(block)}
              </EmailMakerBlock>
            );
          })}
        </div>
      </>
    );
  };

  const showPanelHeaderShadowIfNeeded = (): void => {
    setShowShadow((panelContentRef.current?.scrollTop ?? 0) > 0);
  };

  const trackGA4EventOnDefaultTextAdd = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_ADD_ELEMENT, {[GA4EventParamName.TYPE]: GA4_TRACKING_NAME});
  };

  useEffect(() => {
    panelContentRef.current?.addEventListener('scroll', showPanelHeaderShadowIfNeeded, {passive: true});

    return (): void => {
      panelContentRef.current?.removeEventListener('scroll', showPanelHeaderShadowIfNeeded);
    };
  }, []);

  return (
    <div ref={panelContentRef} className={styles.panelContent}>
      <ElementsPanelHeader headerLabel={headerLabel} showBackButton={showBackButton} onBackButton={onBackButton} showShadow={showShadow} />
      {getDefaultTextBlocks()}
      <EmailComponentTemplates headerLabelOverride={window.i18next.t('pmwjs_templates')} selectedCategoryId={TEXT_TEMPLATE_CATEGORY_ID} key={getUniqueString()} />
    </div>
  );
}
